import React, { useEffect, useMemo, useState } from 'react';
import { ImagePlus, Megaphone, X, Loader2, SendHorizontal } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import AuthImage from '../media/AuthImage';

export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
export const MAX_IMAGE_PIXELS = 24_000_000;
export const TOO_MANY_PIXELS = 'La imagen es demasiado grande en píxeles (máximo 24 megapíxeles): reduce su tamaño e inténtalo de nuevo.';
export const IMAGE_TYPES = ['image/jpeg', 'image/png'];
export const PROMO_KIND = { event: 'Evento', info: 'Informativa' };
const CAPTION_MAX = 1024;

/** Revisión rápida en el navegador (el backend valida de verdad). Devuelve el error o null. */
export function checkImageFile(file) {
  if (!file) return 'Elige una imagen';
  if (!IMAGE_TYPES.includes(file.type)) return 'Solo se permiten imágenes JPG o PNG';
  if (file.size > MAX_IMAGE_BYTES) return 'La imagen supera 5 MB';
  return null;
}

/**
 * Revisa las dimensiones en el navegador antes de subirla (el backend también lo
 * valida). Resuelve con el mensaje de error o null.
 */
export function checkImagePixels(file) {
  return new Promise(resolve => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img.naturalWidth * img.naturalHeight > MAX_IMAGE_PIXELS ? TOO_MANY_PIXELS : null);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve('El archivo no es una imagen JPG o PNG válida'); };
    img.src = url;
  });
}

function useEscape(onClose) {
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
}

/**
 * Botón +: vista previa de la imagen elegida, texto opcional y enviar.
 * La imagen enviada se borra del portal a los 15 días.
 */
export function AttachImageModal({ file, initialCaption = '', onClose, onSend }) {
  const [caption, setCaption] = useState(initialCaption);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(() => checkImageFile(file));
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);
  useEffect(() => {
    let alive = true;
    if (file && !checkImageFile(file)) checkImagePixels(file).then(err => { if (alive && err) setError(err); });
    return () => { alive = false; };
  }, [file]);
  useEscape(onClose);

  async function submit(e) {
    e.preventDefault();
    if (checkImageFile(file)) return;
    const pixelError = await checkImagePixels(file);
    if (pixelError) return setError(pixelError);
    setSending(true);
    setError('');
    try {
      await onSend(file, caption.trim());
      onClose();
    } catch (err) {
      setError(err.message);
      setSending(false);
    }
  }

  return (
    <div style={st.overlay} onClick={onClose}>
      <form style={st.modal} onClick={e => e.stopPropagation()} onSubmit={submit} role="dialog" aria-modal="true" aria-labelledby="attach-title">
        <div style={st.head}>
          <ImagePlus size={18} color="var(--accent)" />
          <h2 id="attach-title" style={st.title}>Enviar imagen</h2>
          <button type="button" style={st.close} onClick={onClose} aria-label="Cerrar"><X size={18} /></button>
        </div>
        {preview && !checkImageFile(file) && <img src={preview} alt="Vista previa" style={st.preview} />}
        <div style={st.muted}>{file?.name} · {file ? (file.size / 1048576).toFixed(1) : 0} MB · JPG o PNG hasta 5 MB. Se envía sin datos de ubicación y se borra del portal a los 15 días.</div>
        <label style={st.label} htmlFor="attach-caption">Texto (opcional)</label>
        <textarea id="attach-caption" style={st.textarea} rows={2} maxLength={CAPTION_MAX} value={caption}
          onChange={e => setCaption(e.target.value)} autoFocus />
        {error && <div style={st.error}>{error}</div>}
        <div style={st.actions}>
          <button type="button" className="ui-hover" style={st.secondary} onClick={onClose}>Cancelar</button>
          <button type="submit" className="ui-primary" style={st.primary} disabled={sending || !!checkImageFile(file)}>
            {sending ? <Loader2 size={15} className="ui-spin" /> : <SendHorizontal size={15} />} Enviar
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Botón "Promociones": imágenes vigentes de la biblioteca (filtro por tipo).
 * El asesor elige una, escribe texto opcional y la envía.
 */
export function PromotionPickerModal({ initialCaption = '', onClose, onSend }) {
  const { apiFetch } = useApi();
  const [data, setData] = useState(null);
  const [kind, setKind] = useState('all');
  const [selected, setSelected] = useState(null);
  const [caption, setCaption] = useState(initialCaption);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  useEscape(onClose);

  useEffect(() => {
    apiFetch('/api/promotions').then(setData).catch(e => setError(e.message));
  }, [apiFetch]);

  const items = (data?.items || []).filter(p => kind === 'all' || p.kind === kind);

  async function submit(e) {
    e.preventDefault();
    if (!selected) return;
    setSending(true);
    setError('');
    try {
      await onSend(selected.id, caption.trim());
      onClose();
    } catch (err) {
      setError(err.message);
      setSending(false);
      // Si venció mientras se elegía, se recarga la lista
      if (err.code === 'PROMOTION_GONE') {
        setSelected(null);
        apiFetch('/api/promotions').then(setData).catch(() => {});
      }
    }
  }

  return (
    <div style={st.overlay} onClick={onClose}>
      <form style={{ ...st.modal, maxWidth: 640 }} onClick={e => e.stopPropagation()} onSubmit={submit} role="dialog" aria-modal="true" aria-labelledby="promo-title">
        <div style={st.head}>
          <Megaphone size={18} color="var(--accent)" />
          <h2 id="promo-title" style={st.title}>Promociones</h2>
          <button type="button" style={st.close} onClick={onClose} aria-label="Cerrar"><X size={18} /></button>
        </div>
        <div style={st.chips} role="tablist" aria-label="Tipo">
          {[['all', 'Todas'], ['event', PROMO_KIND.event], ['info', PROMO_KIND.info]].map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={kind === k}
              style={{ ...st.chip, ...(kind === k ? st.chipActive : {}) }} onClick={() => setKind(k)}>{label}</button>
          ))}
        </div>
        {!data && !error && <div style={st.muted}>Cargando…</div>}
        {data && items.length === 0 && <div style={st.muted}>No hay imágenes vigentes{kind !== 'all' ? ' de este tipo' : ''}. El administrador las sube en Administración → Promociones.</div>}
        <div style={st.grid} role="radiogroup" aria-label="Imágenes">
          {items.map(p => {
            const sel = selected?.id === p.id;
            return (
              <button key={p.id} type="button" role="radio" aria-checked={sel}
                style={{ ...st.card, ...(sel ? st.cardSel : {}) }} onClick={() => setSelected(p)}>
                <div style={st.thumb}><AuthImage url={p.image_url} alt={p.title} maxHeight={110} zoom={false} style={{ width: '100%', height: 110, objectFit: 'cover' }} /></div>
                <div style={st.cardTitle}>{p.title}</div>
                <div style={st.cardMeta}>{PROMO_KIND[p.kind]} · vence {p.days_left === 0 ? 'hoy' : `en ${p.days_left} d`}</div>
              </button>
            );
          })}
        </div>
        <label style={st.label} htmlFor="promo-caption">Texto (opcional)</label>
        <textarea id="promo-caption" style={st.textarea} rows={2} maxLength={CAPTION_MAX} value={caption}
          onChange={e => setCaption(e.target.value)} />
        {error && <div style={st.error}>{error}</div>}
        <div style={st.actions}>
          <button type="button" className="ui-hover" style={st.secondary} onClick={onClose}>Cancelar</button>
          <button type="submit" className="ui-primary" style={st.primary} disabled={!selected || sending}>
            {sending ? <Loader2 size={15} className="ui-spin" /> : <SendHorizontal size={15} />} Enviar
          </button>
        </div>
      </form>
    </div>
  );
}

const st = {
  overlay: { position: 'fixed', inset: 0, background: 'var(--overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 },
  modal: { background: 'var(--raised)', border: '1px solid var(--border)', borderRadius: 16, boxShadow: 'var(--shadow-md)', width: '100%', maxWidth: 460, padding: 20, display: 'flex', flexDirection: 'column', gap: 10, maxHeight: '90vh', overflowY: 'auto' },
  head: { display: 'flex', alignItems: 'center', gap: 10 },
  title: { fontSize: 17, fontWeight: 800, margin: 0, flex: 1, color: 'var(--text)' },
  close: { background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', display: 'flex', padding: 4 },
  preview: { maxWidth: '100%', maxHeight: 280, objectFit: 'contain', borderRadius: 10, alignSelf: 'center', background: 'var(--surface-3)' },
  label: { fontSize: 12, fontWeight: 700, color: 'var(--text-2)', marginTop: 4 },
  muted: { color: 'var(--text-3)', fontSize: 12.5 },
  textarea: { width: '100%', background: 'var(--surface)', border: '1px solid var(--border-strong)', borderRadius: 10, padding: '8px 10px', color: 'var(--text)', fontSize: 13.5, resize: 'vertical', boxSizing: 'border-box' },
  error: { background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', color: 'var(--danger)', borderRadius: 10, padding: '8px 12px', fontSize: 13 },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 },
  secondary: { background: 'var(--surface)', border: '1px solid var(--border-strong)', borderRadius: 10, padding: '8px 14px', fontSize: 13, fontWeight: 600, color: 'var(--text)', cursor: 'pointer' },
  primary: { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--accent)', border: 'none', borderRadius: 10, padding: '8px 16px', fontSize: 13, fontWeight: 700, color: 'var(--on-accent)', cursor: 'pointer' },
  chips: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  chip: { background: 'var(--surface-2)', border: '1px solid var(--border-strong)', borderRadius: 20, padding: '5px 12px', fontSize: 12, color: 'var(--text-2)', cursor: 'pointer' },
  chipActive: { background: 'var(--accent-soft)', border: '1px solid var(--accent-border)', color: 'var(--text)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, maxHeight: 360, overflowY: 'auto', padding: 2 },
  card: { display: 'flex', flexDirection: 'column', gap: 4, textAlign: 'left', border: '1px solid var(--border)', borderRadius: 12, padding: 6, background: 'var(--surface)', cursor: 'pointer', color: 'var(--text)' },
  cardSel: { borderColor: 'var(--accent)', boxShadow: '0 0 0 2px var(--accent-soft)' },
  thumb: { borderRadius: 8, overflow: 'hidden', background: 'var(--surface-3)', height: 110 },
  cardTitle: { fontSize: 12.5, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardMeta: { fontSize: 11, color: 'var(--text-3)' },
};
